"use client";

import { useMemo } from "react";
import { Answer } from "@prisma/client";
import { useFieldArray, useForm } from "react-hook-form";
import { toast } from "react-toastify";
import { RouterInputs, RouterOutputs, api } from "~/utils/api";
import { Label } from "../v3/shadcn/Label";
import { Button } from "../v3/themed/Button";
import { Unpacked } from "~/types/helpers";
import Modal from "../v3/themed/Modal";
import { Input } from "../v3/themed/Input";
import { MultiSelect } from "../shadcn/MultiSelect";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "../shadcn/Select";
import { linkedInSchema, urlSchema } from "~/types/EventTypes";
import { useWhiteLabel } from "~/app/providers";
import { cn } from "~/utils/fontsConfig";

type QuestionnaireOutput = Unpacked<RouterOutputs["questionnaire"]["getAll"]>;

type FormData = Omit<
    RouterInputs["questionnaire"]["addCompletion"],
    "answersv2"
> & {
    answersv2: (Omit<Answer, "createdAt" | "updatedAt" | "isDeleted"> & {
        isDeleted?: boolean;
        id?: string | undefined | null;
    })[];
};
export type QuestionnaireEssential = Pick<
    QuestionnaireOutput,
    "id" | "name" | "questionsv2"
>;

type Props = {
    isVisible: boolean;
    setVisible: (show: boolean) => void;
    onClose?: (show: boolean) => void;
    onCompletion: (
        success: boolean,
        questionnaireCompletionId?: string,
    ) => void;
    questionnaire: QuestionnaireEssential | undefined | null | boolean;
    action?: string;
    eventId?: string;
};

const QuestionnaireModal = ({
    isVisible,
    setVisible,
    onClose,
    onCompletion,
    questionnaire,
    action,
    eventId,
}: Props) => {
    if (typeof questionnaire === "boolean") {
        return null;
    }
    const addCompletion = api.questionnaire.addCompletion.useMutation();
    const whitelabel = useWhiteLabel();
    const customization = whitelabel?.customization;

    const defaultValues = useMemo(() => {
        return questionnaire
            ? {
                  answersv2: Array.from(
                      Array(questionnaire.questionsv2.length),
                  ).map((_, index) => {
                      return {
                          text: "",
                          questionId:
                              questionnaire.questionsv2[index]?.id || "",
                      };
                  }),
              }
            : { answersv2: [] };
    }, [questionnaire]);

    const {
        register,
        handleSubmit,
        formState,
        control,
        reset,
        trigger,
        setValue,
    } = useForm<FormData>({ defaultValues });

    const { fields } = useFieldArray({
        control,
        name: "answersv2",
        rules: {
            minLength: questionnaire?.questionsv2.length,
        },
    });

    const onSubmit = async (data: FormData) => {
        if (!questionnaire) {
            return;
        }

        toast.loading(`Adding questionnaire answers`, {
            toastId: "add-questionnaire",
        });

        await addCompletion.mutateAsync(
            {
                questionnaireId: questionnaire.id,
                answersv2: data.answersv2.filter((a) => !a.isDeleted),
                eventId,
            },
            {
                onSuccess: () => {
                    toast.success(`Added Questionnaire Answers`, {
                        autoClose: 1000,
                    });
                },
                onError: () => {
                    toast.error(`Error Adding Questionnaire Answers`, {
                        autoClose: 1000,
                    });
                },
                onSettled: async (data, error) => {
                    toast.dismiss("add-questionnaire");
                    reset();
                    setVisible(false);
                    await onCompletion(!error, data?.id);
                },
            },
        );
    };

    if (!isVisible) {
        return <></>;
    }

    if (!questionnaire) {
        return null;
    }

    const onOpenChange = (show: boolean) => {
        setVisible(show);
        if (onClose) {
            onClose(show);
        }
    };

    return (
        <Modal
            open={isVisible}
            onOpenChange={onOpenChange}
            title="Answer Questions"
            customization={customization}
            description={
                "Community owner requires answers to following questions" +
                (action ? ` to ${action}.` : ".")
            }
            footer={
                <div className="mt-4 flex flex-row gap-2">
                    <Button
                        className="ml-4 text-background hover:text-tertiary"
                        variant="secondary"
                        onClick={() => {
                            setVisible(false);
                            onCompletion(false, undefined);
                        }}
                    >
                        Nevermind
                    </Button>
                    <Button
                        variant="default"
                        className={cn(
                            "w-[100px] rounded-[26px] border border-background",
                            customization?.buttons?.default,
                        )}
                        disabled={!formState.isValid || addCompletion.isLoading}
                        onClick={handleSubmit(onSubmit)}
                    >
                        Submit
                    </Button>
                </div>
            }
        >
            <div className="flex flex-col justify-start gap-4 text-start">
                <div className="flex flex-col gap-2 border-b border-v3-N400 pb-4 text-left text-[16px]">
                    <Label className={cn(customization?.labelClassName)}>
                        {questionnaire.name}
                    </Label>
                    <div className="flex flex-col justify-start text-start">
                        {fields.map((field, index) => (
                            <div
                                key={index}
                                className="mt-6 text-left font-mono text-[16px] font-normal leading-normal"
                            >
                                <p
                                    className={cn(
                                        customization?.labelClassName,
                                    )}
                                >
                                    {questionnaire.questionsv2[index]?.question}
                                    {questionnaire.questionsv2[index]
                                        ?.isRequired && (
                                        <span className="text-red-500">*</span>
                                    )}
                                </p>
                                <Label
                                    htmlFor={`answersv2-${index}`}
                                    className={cn(
                                        customization?.descriptionClassName,
                                    )}
                                >
                                    Answer
                                </Label>
                                <div className="flex space-x-2">
                                    {questionnaire.questionsv2[index]?.type ===
                                        "text" && (
                                        <div className="flex w-full space-x-2">
                                            <Input
                                                {...register(
                                                    `answersv2.${index}.text`,
                                                    {
                                                        required:
                                                            questionnaire
                                                                .questionsv2[
                                                                index
                                                            ]?.isRequired,
                                                    },
                                                )}
                                                onBlur={() => {
                                                    trigger(
                                                        `answersv2.${index}.text`,
                                                    );
                                                }}
                                                id={`answersv2-${index}`}
                                                key={field.id}
                                                type="text"
                                                className={cn(
                                                    "w-full",
                                                    customization?.inputClassName,
                                                )}
                                            />
                                        </div>
                                    )}
                                    {questionnaire.questionsv2[index]?.type ===
                                        "text-url" && (
                                        <div className="flex w-full space-x-2">
                                            <Input
                                                {...register(
                                                    `answersv2.${index}.text`,
                                                    {
                                                        required:
                                                            questionnaire
                                                                .questionsv2[
                                                                index
                                                            ]?.isRequired,
                                                        validate: (val) => {
                                                            const result =
                                                                urlSchema.safeParse(
                                                                    val,
                                                                );
                                                            return (
                                                                result.success ||
                                                                result.error
                                                                    ?.issues[0]
                                                                    ?.message
                                                            );
                                                        },
                                                    },
                                                )}
                                                onBlur={() => {
                                                    trigger(
                                                        `answersv2.${index}.text`,
                                                    );
                                                }}
                                                id={`answersv2-${index}`}
                                                key={field.id}
                                                type="text"
                                                className={cn(
                                                    "w-full",
                                                    customization?.inputClassName,
                                                )}
                                            />
                                        </div>
                                    )}
                                    {questionnaire.questionsv2[index]?.type ===
                                        "text-linkedin-url" && (
                                        <div className="flex w-full space-x-2">
                                            <Input
                                                {...register(
                                                    `answersv2.${index}.text`,
                                                    {
                                                        required:
                                                            questionnaire
                                                                .questionsv2[
                                                                index
                                                            ]?.isRequired,
                                                        validate: (val) => {
                                                            const result =
                                                                linkedInSchema.safeParse(
                                                                    val,
                                                                );
                                                            return (
                                                                result.success ||
                                                                result.error
                                                                    ?.issues[0]
                                                                    ?.message
                                                            );
                                                        },
                                                    },
                                                )}
                                                onBlur={() => {
                                                    trigger(
                                                        `answersv2.${index}.text`,
                                                    );
                                                }}
                                                id={`answersv2-${index}`}
                                                key={field.id}
                                                type="text"
                                                className={cn(
                                                    "w-full",
                                                    customization?.inputClassName,
                                                )}
                                            />
                                        </div>
                                    )}
                                    {questionnaire.questionsv2[index]?.type ===
                                        "select" && (
                                        <div className="flex w-full space-x-2">
                                            <MultiSelect
                                                className={cn(
                                                    "items-center",
                                                    customization?.inputClassName,
                                                )}
                                                options={(
                                                    questionnaire.questionsv2[
                                                        index
                                                    ]?.selectValues ?? []
                                                ).map((o) => ({
                                                    label: o,
                                                    value: o,
                                                }))}
                                                onChange={(e) => {
                                                    // this is a little hacky, but simplifies a lot
                                                    setValue(
                                                        `answersv2.${index}.text`,
                                                        e.join(","),
                                                    );
                                                    trigger(
                                                        `answersv2.${index}.text`,
                                                    );
                                                }}
                                            />
                                        </div>
                                    )}
                                    {questionnaire.questionsv2[index]?.type ===
                                        "single-select" && (
                                        <div className="flex w-full space-x-2">
                                            {/* single dropdown select for single select */}
                                            <Select
                                                onValueChange={(e) => {
                                                    setValue(
                                                        `answersv2.${index}.text`,
                                                        e,
                                                    );
                                                    trigger(
                                                        `answersv2.${index}.text`,
                                                    );
                                                }}
                                            >
                                                <SelectTrigger
                                                    className={cn(
                                                        "cursor-pointer rounded-md bg-v3-neutral/90 px-4 py-2",
                                                        customization
                                                            ?.selectInput
                                                            ?.inputClassName,
                                                    )}
                                                >
                                                    <SelectValue
                                                        placeholder={
                                                            "Choose an option"
                                                        }
                                                        className={cn(
                                                            customization
                                                                ?.selectInput
                                                                ?.inputClassName,
                                                        )}
                                                    />
                                                </SelectTrigger>
                                                <SelectContent
                                                    className={cn(
                                                        customization
                                                            ?.selectInput
                                                            ?.listClassName,
                                                    )}
                                                >
                                                    {questionnaire.questionsv2[
                                                        index
                                                    ]?.selectValues?.map(
                                                        (o) => (
                                                            <SelectItem
                                                                key={o}
                                                                value={o}
                                                                className={cn(
                                                                    "flex items-center gap-2",
                                                                    customization
                                                                        ?.selectInput
                                                                        ?.itemClassName,
                                                                )}
                                                            >
                                                                <span>{o}</span>
                                                            </SelectItem>
                                                        ),
                                                    )}
                                                </SelectContent>
                                            </Select>
                                        </div>
                                    )}
                                </div>
                                {formState.errors.answersv2?.[index] && (
                                    <span className="text-sm text-red-500">
                                        {formState.errors.answersv2[index]?.text
                                            ?.message ||
                                            "This field is required"}
                                    </span>
                                )}
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </Modal>
    );
};

export default QuestionnaireModal;
